// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Component,
  Construction,
  Eye,
  Filter,
  Info,
  Loader2,
  Mail,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Settings,
  Sparkles,
  Star,
  Trash2,
  Upload,
  Wrench,
} from "lucide-react";
import { type ComponentType, lazy, Suspense, type SVGProps, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  ChainPlusIcon,
  CloudPlusIcon,
  DatabasePlusIcon,
  FocalLogoIcon,
  GroupPlusIcon,
  ImportIcon,
  ImportPlusIcon,
  ImportRetriggerIcon,
  KeyPlusIcon,
  MemoryStickPlusIcon,
  RealmPlusIcon,
  SettingsCompareIcon,
  StorePlusIcon,
  Sushi2Icon,
  SushiPlusIcon,
} from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { CopyableKey } from "@/components/shared/copyable-key";
import { DatePicker } from "@/components/shared/date-picker";
import { DateTimeValue, DateValue, TimeValue } from "@/components/shared/datetime-value";
import { FilterMultiSelect } from "@/components/shared/filter-multi-select";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { HealthStatusBadge } from "@/components/shared/health-status-badge";
import { IdPill } from "@/components/shared/id-pill";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { KeyChip } from "@/components/shared/key-chip";
import { LinearIcon } from "@/components/shared/linear-icon";
import { ProgressBar } from "@/components/shared/progress-bar";
import { RowId } from "@/components/shared/row-id";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { ScheduleLabelChip, ScheduleLabelLegend } from "@/components/shared/schedule-label-chip";
import { ScheduleTimeline } from "@/components/shared/schedule-timeline";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { UserLink } from "@/components/shared/user-link";
import { VendorLink } from "@/components/shared/vendor-link";
import { VirtualMultiSelectPopover } from "@/components/shared/virtual-multi-select-popover";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { OpeningHoursList } from "@/features/stores/opening-hours-list";
import type { TimelineSegment } from "@/features/stores/schedule/types";
import { ACCENT_OPTIONS, useAccent, useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/showcase/")({
  component: ShowcasePage,
});

// Live swatches for the accent-driven tokens. `--destructive` is in the list on purpose: it's
// the nearest neighbour to a red `--primary`, so keep them side by side to check the contrast.
// Lazy for the same reason the last-login dialog is: the showcase lives in the eager `index` chunk,
// so a static import here would drag @visx and its d3 dependencies back into the first load.
const StatusAreaChart = lazy(() =>
  import("@/components/charts/status-area-chart").then((m) => ({ default: m.StatusAreaChart })),
);

const CHART_SERIES = [
  { key: "deleted", color: "var(--destructive)", gradientId: "showcaseDeleted" },
  { key: "inactive", color: "var(--muted-foreground)", gradientId: "showcaseInactive" },
  { key: "active", color: "var(--primary)", gradientId: "showcaseActive" },
] as const;

const CHART_BUCKETS = Array.from({ length: 26 }, (_, index) => {
  const wave = 0.5 + 0.5 * Math.sin((index / 26) * Math.PI * 3);
  return {
    key: index,
    label: `Day ${index + 1}`,
    active: Math.round(40 + wave * 90),
    inactive: Math.round(12 + wave * 24),
    deleted: Math.round(4 + wave * 9),
  };
});

// Kept as its own component so the replay state stays out of the main showcase body.
function StatusAreaChartDemo() {
  const [replay, setReplay] = useState(0);
  return (
    <div className="flex flex-col gap-2">
      <div className="h-64 w-full">
        <Suspense fallback={<InlineSpinner className="h-full" />}>
          <StatusAreaChart
            key={replay}
            buckets={CHART_BUCKETS}
            series={CHART_SERIES}
            animate
            statusLabel={(status) => status}
            formatCount={(value) => String(value)}
            totalLabel="Total"
          />
        </Suspense>
      </div>
      <Button variant="outline" size="sm" className="self-start" onClick={() => setReplay(replay + 1)}>
        Replay intro
      </Button>
    </div>
  );
}

const ACCENT_TOKENS = [
  { label: "--primary", className: "bg-primary" },
  { label: "--ring", className: "bg-ring" },
  // `--sidebar` itself stays neutral on every accent — the panel's colour comes from the
  // bottom-left `--sidebar-wash` ramp, so that's what belongs here.
  { label: "--sidebar-wash", className: "bg-[rgb(var(--sidebar-wash))]" },
  { label: "--sidebar-accent", className: "bg-sidebar-accent" },
  { label: "--sidebar-accent-hover", className: "bg-sidebar-accent-hover" },
  { label: "--destructive", className: "bg-destructive" },
] as const;

// Custom Lucide-compatible SVG icons from @/components/icons (kept in sync with that barrel).
const CUSTOM_ICONS: { name: string; Icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
  { name: "ChainPlusIcon", Icon: ChainPlusIcon },
  { name: "CloudPlusIcon", Icon: CloudPlusIcon },
  { name: "DatabasePlusIcon", Icon: DatabasePlusIcon },
  { name: "FocalLogoIcon", Icon: FocalLogoIcon },
  { name: "GroupPlusIcon", Icon: GroupPlusIcon },
  { name: "ImportIcon", Icon: ImportIcon },
  { name: "ImportPlusIcon", Icon: ImportPlusIcon },
  { name: "ImportRetriggerIcon", Icon: ImportRetriggerIcon },
  { name: "KeyPlusIcon", Icon: KeyPlusIcon },
  { name: "MemoryStickPlusIcon", Icon: MemoryStickPlusIcon },
  { name: "RealmPlusIcon", Icon: RealmPlusIcon },
  { name: "SettingsCompareIcon", Icon: SettingsCompareIcon },
  { name: "StorePlusIcon", Icon: StorePlusIcon },
  { name: "Sushi2Icon", Icon: Sushi2Icon },
  { name: "SushiPlusIcon", Icon: SushiPlusIcon },
];

// Mirrors a list page's FILTER_DEFAULTS: `page`/`pageSize` are the two keys a preset never carries.
const SHOWCASE_PRESET_DEFAULTS = {
  onlyActive: false,
  region: "",
  page: 0,
  pageSize: 20,
};

function ShowcasePage() {
  const [switchOn, setSwitchOn] = useState(false);
  const [singleSlider, setSingleSlider] = useState([50]);
  const [rangeSlider, setRangeSlider] = useState([360, 1320]);
  const [selectValue, setSelectValue] = useState("");
  const [dateValue, setDateValue] = useState<string | undefined>(undefined);
  const [pastDateValue, setPastDateValue] = useState<string | undefined>(undefined);
  const [showcaseDateFormat] = useDateFormat();
  const [accent, setAccent] = useAccent();
  const [filterToggleActive, setFilterToggleActive] = useState(false);
  const [showcasePresetFilters, setShowcasePresetFilters] = useState(SHOWCASE_PRESET_DEFAULTS);
  const [filterSelectValue, setFilterSelectValue] = useState("");
  const [filterMultiSelectValues, setFilterMultiSelectValues] = useState<Set<string> | null>(null);
  const [wizardDialogOpen, setWizardDialogOpen] = useState(false);
  const [wizardDestructiveDialogOpen, setWizardDestructiveDialogOpen] = useState(false);
  const [virtualSelectValue, setVirtualSelectValue] = useState("");
  const [virtualMultiSelectValues, setVirtualMultiSelectValues] = useState<string[]>([]);
  const [scheduleSegments, setScheduleSegments] = useState<TimelineSegment[]>([
    { start: 360, end: 540, label: "off" },
    { start: 540, end: 660, label: "human" },
    { start: 660, end: 1200, label: "model" },
    { start: 1200, end: 1320, label: "qr" },
  ]);
  const [selectedScheduleLabel, setSelectedScheduleLabel] = useState<"model" | "human" | "qr" | "off">("model");

  // Demonstrates the validation-error layout (FormLabelRow) on the Textarea field below.
  const errorForm = useForm({ defaultValues: { textarea: "ASDAdssd" } });
  useEffect(
    function seedShowcaseFieldError() {
      errorForm.setError("textarea", { message: "Only lowercase letters, digits and underscores are allowed" });
    },
    [errorForm],
  );

  return (
    <div>
      <PageHeader title="Component Showcase" icon={Component} />
      <div className="max-w-4xl space-y-8 p-6">
        {/* Typography & Basic */}
        <Card>
          <CardHeader>
            <CardTitle>Typography &amp; Basic</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">
                &lt;h1&gt; &lt;h2&gt; &lt;h3&gt; &lt;h4&gt; &lt;p&gt;
              </code>
            </CardAction>
            <CardDescription>Heading levels, body text, and muted secondary text</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <h1 className="text-4xl font-bold tracking-tight">Heading 1 - Page Title</h1>
            <h2 className="text-3xl font-semibold tracking-tight">Heading 2 - Section Title</h2>
            <h3 className="text-2xl font-semibold tracking-tight">Heading 3 - Subsection</h3>
            <h4 className="text-xl font-semibold tracking-tight">Heading 4 - Card Title</h4>
            <p className="leading-7">
              This is a paragraph of body text. It demonstrates the default text styling used throughout the application
              for readable content blocks.
            </p>
            <p className="text-sm text-muted-foreground">
              This is muted text, commonly used for descriptions, hints, and secondary information.
            </p>
          </CardContent>
        </Card>

        {/* Accent Themes */}
        <Card>
          <CardHeader>
            <CardTitle>Accent Themes</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">useAccent()</code>
            </CardAction>
            <CardDescription>
              Brand accent, a second axis independent of light/dark. Switch it here to check the resolved tokens. The
              red accent is deliberately muted so it stays distinguishable from{" "}
              <code className="font-mono">--destructive</code> (parked alongside for exactly that comparison), and
              status colours (below) never follow the accent at all.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {ACCENT_OPTIONS.map((option) => (
                <Button
                  key={option}
                  variant={accent === option ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAccent(option)}
                >
                  {option}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              {ACCENT_TOKENS.map((token) => (
                <div key={token.className} className="space-y-1">
                  <div className={cn("size-12 rounded-md border", token.className)} />
                  <code className="block text-[10px] font-mono text-muted-foreground">{token.label}</code>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Badges */}
        <Card>
          <CardHeader>
            <CardTitle>Badges</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Badge&gt;</code>
            </CardAction>
            <CardDescription>Small labels for status, categories, and counts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="default">Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="destructive">Destructive</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Buttons */}
        <Card>
          <CardHeader>
            <CardTitle>Buttons</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Button&gt;</code>
            </CardAction>
            <CardDescription>All variants and sizes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="default">Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="link">Link</Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button>
                <Mail className="h-4 w-4" />
                With Icon
              </Button>
              <Button>
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button disabled>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </Button>
              <Button disabled>Disabled</Button>
            </div>
          </CardContent>
        </Card>

        {/* Cards */}
        <Card>
          <CardHeader>
            <CardTitle>Card</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Card&gt;</code>
            </CardAction>
            <CardDescription>Grouped content container with header, body, and optional footer</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              Cards are used extensively throughout the application to group related content. They provide visual
              separation and hierarchy.
            </p>
          </CardContent>
        </Card>

        {/* ChainLink */}
        <Card>
          <CardHeader>
            <CardTitle>Chain Link</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;ChainLink&gt;</code>
            </CardAction>
            <CardDescription>
              Linked chain reference pointing to the chain detail page. Renders the chain logo when registered (and
              enabled), otherwise the legible name with a leading chain icon — hidden via showIcon=false where the
              context is unambiguous (e.g. the chains list). Logos can ship a dark-mode variant (e.g. Boots) — toggle
              the theme to see it swap.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              <ChainLink chainName="acme" legibleName="Acme Markets" />
              <ChainLink chainName="acme" legibleName="Acme Markets (no icon)" showIcon={false} />
              <ChainLink chainName="boots" legibleName="Boots" />
            </div>
          </CardContent>
        </Card>

        {/* StatusAreaChart */}
        <Card>
          <CardHeader>
            <CardTitle>Charts</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;StatusAreaChart&gt;</code>
            </CardAction>
            <CardDescription>
              Stacked area chart of per-status counts over labelled time buckets, built on visx. Series are ordered
              bottom-to-top of the stack and fill with a gradient derived from their theme token, so the palette follows
              the accent and dark mode. Hover to get a dot on every series line, a cursor rule and a tooltip that tracks
              the pointer on both axes. The intro wipes left-to-right; the first and last x labels anchor inward so they
              cannot clip. Loaded lazily — visx sits in its own <code>vendor-visx</code> chunk.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StatusAreaChartDemo />
          </CardContent>
        </Card>

        {/* CodeHighlight */}
        <Card>
          <CardHeader>
            <CardTitle>Code Highlight</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;CodeHighlight&gt;</code>
            </CardAction>
            <CardDescription>
              Syntax-highlighted code viewer with copy support — supports CEL, JSON, Regexp, and SQL. Pass{" "}
              <code className="font-mono">copyable={"{false}"}</code> to drop the hover copy button on short snippets.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">CEL</Label>
              <CodeHighlight
                value={
                  '// C1 \n(task.type == "low" && product.department in ["02", "05"]) ? false : \n// C2\n(task.type == "zero_inventory_ins" && product.department == "05") ? false : \n// C-fallback-allow\ntrue'
                }
                lang="cel"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                CEL — no copy button
              </Label>
              <CodeHighlight
                value={'task.type in ["calculated_low", "manual"] && store.open'}
                lang="cel"
                copyable={false}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">JSON</Label>
              <CodeHighlight
                value={JSON.stringify({
                  chain: "acme-foods",
                  storeCount: 42,
                  active: true,
                  regions: ["EU", "NA"],
                })}
                lang="json"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Regexp</Label>
              <CodeHighlight
                value={"^(?:store_(?<chain>[a-z]+)_(?<code>\\d{3,6}))_\\d{4}-\\d{2}-\\d{2}\\.csv$"}
                lang="regexp"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">SQL</Label>
              <CodeHighlight
                value={
                  "SELECT s.store_name, c.chain_name, s.legible_name, s.region, s.timezone, s.created_at, s.updated_at, s.is_active\nFROM stores s\nJOIN chains c ON c.chain_id = s.chain_id\nWHERE s.is_active = true\nORDER BY s.legible_name;"
                }
                lang="sql"
              />
            </div>
          </CardContent>
        </Card>

        {/* CopyableKey */}
        <Card>
          <CardHeader>
            <CardTitle>Copyable Key</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;CopyableKey&gt;</code>
            </CardAction>
            <CardDescription>
              Muted monospace key shown as a name sub-line on list pages when the dedicated Key column is hidden. Click
              anywhere to copy; a copy icon sits on the right.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start gap-2">
              <CopyableKey value="acme_foods" />
              <CopyableKey value="0badf00d-1234-4caf-9e21-abcdef012345" />
            </div>
          </CardContent>
        </Card>

        {/* Data Display - Table */}
        <Card>
          <CardHeader>
            <CardTitle>Data Table</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;DataTable&gt;</code>
            </CardAction>
            <CardDescription>Sortable, paginated table with search, filters, and row actions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-sidebar hover:bg-sidebar">
                    <TableHead className="rounded-tl-md pl-4">ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right rounded-tr-md">Stores</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="h-12">
                    <TableCell className="pl-4">1</TableCell>
                    <TableCell>Acme Foods</TableCell>
                    <TableCell>
                      <StatusIndicator active={true} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">128</TableCell>
                  </TableRow>
                  <TableRow className="h-12">
                    <TableCell className="pl-4">2</TableCell>
                    <TableCell>Beta Groceries</TableCell>
                    <TableCell>
                      <StatusIndicator active={true} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">56</TableCell>
                  </TableRow>
                  <TableRow className="h-12">
                    <TableCell className="pl-4">3</TableCell>
                    <TableCell>Charlie Market</TableCell>
                    <TableCell>
                      <StatusIndicator active={false} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">12</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => toast.info("Refreshing...")}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <p className="text-sm text-muted-foreground">Showing 1–3 of 3</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-9 w-9" disabled>
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9" disabled>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm tabular-nums px-2">1 / 1</span>
                <Button variant="outline" size="icon" className="h-9 w-9" disabled>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9" disabled>
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date & Time Values */}
        <Card>
          <CardHeader>
            <CardTitle>Date &amp; Time Values</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">
                &lt;DateValue&gt; &lt;TimeValue&gt; &lt;DateTimeValue&gt;
              </code>
            </CardAction>
            <CardDescription>
              Dates, times and datetimes rendered in the Cascadia Mono face for column alignment. Empty values show a
              muted dash. Not for timezones or durations. Date order follows the viewer&apos;s{" "}
              <strong>Date format</strong> preference (profile → Appearance), which defaults to the host OS regional
              settings — never the UI language. There is deliberately no <code>locale</code> prop.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-sm">
              <span className="text-muted-foreground">DateValue</span>
              <DateValue value="2026-01-15T09:30:00Z" />
              <span className="text-muted-foreground">TimeValue</span>
              <TimeValue value="2026-01-15T09:30:00Z" />
              <span className="text-muted-foreground">DateTimeValue</span>
              <DateTimeValue value="2026-01-15T09:30:00Z" />
              <span className="text-muted-foreground">Empty</span>
              <DateTimeValue value={null} />
              <span className="text-muted-foreground">Active preference</span>
              <span className="font-mono">{showcaseDateFormat}</span>
            </div>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Card>
          <CardHeader>
            <CardTitle>Dialog</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Dialog&gt; &lt;WizardPane&gt;</code>
            </CardAction>
            <CardDescription>
              Modal dialogs with decorative side panel in default and destructive variants
            </CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2">
            <Dialog open={wizardDialogOpen} onOpenChange={setWizardDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">Default Wizard</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
                <div className="flex flex-row">
                  <WizardPane icon={Sparkles} />
                  <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                    <DialogHeader>
                      <DialogTitle>Create Something</DialogTitle>
                      <DialogDescription>
                        This dialog uses a WizardPane with the default green variant, typically used for creation or
                        confirmation flows.
                      </DialogDescription>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">Form fields would go here.</p>
                  </div>
                </div>
                <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
                  <Button variant="outline" onClick={() => setWizardDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={() => setWizardDialogOpen(false)}>
                    <Sparkles className="h-4 w-4" />
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={wizardDestructiveDialogOpen} onOpenChange={setWizardDestructiveDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">Destructive Wizard</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
                <div className="flex flex-row">
                  <WizardPane variant="destructive" icon={Trash2} />
                  <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                    <DialogHeader>
                      <DialogTitle>Delete Item</DialogTitle>
                      <DialogDescription>
                        This dialog uses a WizardPane with the destructive red variant, used for deletion or dangerous
                        operations.
                      </DialogDescription>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">Are you sure? This action cannot be undone.</p>
                  </div>
                </div>
                <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
                  <Button variant="outline" onClick={() => setWizardDestructiveDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={() => setWizardDestructiveDialogOpen(false)}>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Dropdown Menu */}
        <Card>
          <CardHeader>
            <CardTitle>Dropdown Menu</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;DropdownMenu&gt;</code>
            </CardAction>
            <CardDescription>Context menu with items, separators, and nested submenus</CardDescription>
          </CardHeader>
          <CardContent>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={-32} className="dropdown-overlap">
                <DropdownMenuItem className="bg-sidebar-accent-hover">
                  <Star className="h-4 w-4" />
                  Selected
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => toast.info("View selected")}>
                  <Eye className="h-4 w-4" />
                  View
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => toast.info("Edit selected")}>
                  <Settings className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Upload className="h-4 w-4" />
                    Export
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onSelect={() => toast.info("CSV export")}>As CSV</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => toast.info("JSON export")}>As JSON</DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem
                  onSelect={() => toast.warning("Work in progress…")}
                  className="text-amber-600 underline focus:text-amber-600 dark:text-amber-400 dark:focus:text-amber-400"
                >
                  <Wrench className="h-4 w-4" />
                  <Construction className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                  Not ready
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => toast.error("Delete selected")}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardContent>
        </Card>

        {/* FilterMultiSelect */}
        <Card>
          <CardHeader>
            <CardTitle>Filter Multi Select</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;FilterMultiSelect&gt;</code>
            </CardAction>
            <CardDescription>Multi-value searchable dropdown filter with select all support</CardDescription>
          </CardHeader>
          <CardContent>
            <FilterMultiSelect
              label="Department"
              values={filterMultiSelectValues}
              onChange={setFilterMultiSelectValues}
              options={[
                { label: "100 Produce", value: "100" },
                { label: "200 Dairy", value: "200" },
                { label: "300 Bakery", value: "300" },
                { label: "400 Meat", value: "400" },
                { label: "500 Frozen", value: "500" },
              ]}
              searchPlaceholder="Search departments..."
              emptyText="No departments found."
            />
          </CardContent>
        </Card>

        {/* FilterPresets */}
        <Card>
          <CardHeader>
            <CardTitle>Filter Presets</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;FilterPresets&gt;</code>
            </CardAction>
            <CardDescription>
              Saves a page's filters, search and sort under a name, in the table toolbar beside Tools. Applying one
              always returns to the first page; page size is never captured. Stored per page in localStorage, so this
              example shares nothing with the real lists.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <FilterToggle
                active={showcasePresetFilters.onlyActive}
                onChange={(onlyActive) => setShowcasePresetFilters((prev) => ({ ...prev, onlyActive }))}
                icon={Filter}
                label="Active Only"
              />
              <FilterSelect
                label="Region"
                value={showcasePresetFilters.region}
                onChange={(region) => setShowcasePresetFilters((prev) => ({ ...prev, region }))}
                options={[
                  { label: "Europe", value: "eu" },
                  { label: "North America", value: "na" },
                ]}
                searchPlaceholder="Search regions..."
                emptyText="No regions found."
              />
              <FilterPresets
                storageKey="showcase-presets"
                defaults={SHOWCASE_PRESET_DEFAULTS}
                current={showcasePresetFilters}
                onApply={setShowcasePresetFilters}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Current state: <code className="font-mono">{JSON.stringify(showcasePresetFilters)}</code>
            </p>
          </CardContent>
        </Card>

        {/* FilterSelect */}
        <Card>
          <CardHeader>
            <CardTitle>Filter Select</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;FilterSelect&gt;</code>
            </CardAction>
            <CardDescription>Searchable dropdown filter for data table toolbars</CardDescription>
          </CardHeader>
          <CardContent>
            <FilterSelect
              label="Region"
              value={filterSelectValue}
              onChange={setFilterSelectValue}
              options={[
                { label: "Europe", value: "eu" },
                { label: "North America", value: "na" },
                { label: "Asia Pacific", value: "apac" },
              ]}
              searchPlaceholder="Search regions..."
              emptyText="No regions found."
            />
          </CardContent>
        </Card>

        {/* FilterToggle */}
        <Card>
          <CardHeader>
            <CardTitle>Filter Toggle</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;FilterToggle&gt;</code>
            </CardAction>
            <CardDescription>Toggle button for boolean filters in data table toolbars</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2">
            <FilterToggle
              active={filterToggleActive}
              onChange={setFilterToggleActive}
              icon={Filter}
              label="Active Only"
            />
            <FilterToggle active={false} onChange={() => {}} icon={Filter} label="Loading" loading />
            <FilterToggle active={false} onChange={() => {}} icon={Filter} label="Disabled" disabled />
          </CardContent>
        </Card>

        {/* Health Status Badge */}
        <Card>
          <CardHeader>
            <CardTitle>Health Status Badge</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;HealthStatusBadge&gt;</code>
            </CardAction>
            <CardDescription>
              Grade for a single store-health check — pass, warn and fail, plus info for an ungraded measurement and
              skip for a check that could not run.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <HealthStatusBadge status="pass" />
              <HealthStatusBadge status="warn" />
              <HealthStatusBadge status="fail" />
              <HealthStatusBadge status="info" />
              <HealthStatusBadge status="skip" />
            </div>
          </CardContent>
        </Card>

        {/* Icons */}
        <Card>
          <CardHeader>
            <CardTitle>Icons</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">@/components/icons</code>
            </CardAction>
            <CardDescription>
              Custom Lucide-compatible SVG icons — accept all SVG props, inherit currentColor, and size via className.
              Mostly “+” badge glyphs for create actions, plus a few domain marks.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {CUSTOM_ICONS.map(({ name, Icon }) => (
                <div
                  key={name}
                  className="flex flex-col items-center gap-2 rounded-md border p-3 text-center text-foreground"
                >
                  <Icon className="h-6 w-6" />
                  <code className="text-[10px] font-mono text-muted-foreground break-all">{name}</code>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* IdPill */}
        <Card>
          <CardHeader>
            <CardTitle>ID Pill</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;IdPill&gt;</code>
            </CardAction>
            <CardDescription>Compact pill showing numeric or string IDs with copy-on-click</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <IdPill id={42} />
              <IdPill id={1337} />
              <IdPill id="abc-def-123" />
            </div>
          </CardContent>
        </Card>

        {/* Inline Spinner */}
        <Card>
          <CardHeader>
            <CardTitle>Inline Spinner</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;InlineSpinner&gt;</code>
            </CardAction>
            <CardDescription>
              Block-style loading widget — pulsing Focal logo tile with an optional label
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <InlineSpinner label="Loading..." />
            <InlineSpinner />
          </CardContent>
        </Card>

        {/* Inputs & Forms */}
        <Card>
          <CardHeader>
            <CardTitle>Inputs &amp; Forms</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">
                &lt;Input&gt; &lt;Textarea&gt; &lt;Select&gt; &lt;Switch&gt;
              </code>
            </CardAction>
            <CardDescription>
              Text inputs, textareas, dropdowns, date pickers, toggles, and the validation-error label row
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="text-input">Text Input</Label>
                <Input id="text-input" placeholder="Enter text..." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="disabled-input">Disabled Input</Label>
                <Input id="disabled-input" disabled value="Cannot edit this" />
              </div>
            </div>
            <Form {...errorForm}>
              <FormField
                control={errorForm.control}
                name="textarea"
                render={({ field }) => (
                  <FormItem>
                    <FormLabelRow label="Textarea (with error)" />
                    <FormControl>
                      <Textarea placeholder="Write something longer here..." rows={3} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </Form>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Select</Label>
                <Select value={selectValue} onValueChange={setSelectValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="option-1">Option 1</SelectItem>
                    <SelectItem value="option-2">Option 2</SelectItem>
                    <SelectItem value="option-3">Option 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date Picker</Label>
                <DatePicker
                  value={dateValue}
                  onChange={setDateValue}
                  placeholder="Pick a date"
                  fromYear={2020}
                  toYear={2030}
                />
              </div>
              <div className="space-y-2">
                <Label>Date Picker (past only)</Label>
                {/* `disableAfter` / `disableBefore` grey out days; `toYear` alone only bounds
                    month navigation, which still lets a future day be clicked. */}
                <DatePicker
                  value={pastDateValue}
                  onChange={setPastDateValue}
                  placeholder="Pick a past day"
                  disableAfter={new Date()}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="switch" checked={switchOn} onCheckedChange={setSwitchOn} />
              <Label htmlFor="switch">Switch is {switchOn ? "on" : "off"}</Label>
            </div>
          </CardContent>
        </Card>

        {/* KeyChip */}
        <Card>
          <CardHeader>
            <CardTitle>Key Chip</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;KeyChip&gt;</code>
            </CardAction>
            <CardDescription>Monospace key label with optional chain/store color coding</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <KeyChip label="acme-foods" variant="chain" />
              <KeyChip label="store-berlin-001" variant="store" />
              <KeyChip label="default-key" />
            </div>
          </CardContent>
        </Card>

        {/* Linear Icon */}
        <Card>
          <CardHeader>
            <CardTitle>Linear Icon</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;LinearIcon&gt;</code>
            </CardAction>
            <CardDescription>Monochrome Linear logo (inherits text color via currentColor)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <LinearIcon className="size-4" />
              <LinearIcon className="size-6 text-primary" />
              <LinearIcon className="size-8 text-muted-foreground" />
              <a
                href="https://linear.app/focalsystems/issue/CIN-1"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <LinearIcon className="size-3.5" />
                CIN-1
              </a>
            </div>
          </CardContent>
        </Card>

        {/* ProgressBar */}
        <Card>
          <CardHeader>
            <CardTitle>Progress Bar</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;ProgressBar&gt;</code>
            </CardAction>
            <CardDescription>
              Determinate track for uploads and long-running back-end tasks — pair it with an{" "}
              <code className="font-mono">InlineSpinner</code> label
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center gap-2">
              <InlineSpinner label="Preparing store 3 of 8…" className="py-0" />
              <ProgressBar value={3 / 8} className="max-w-[16rem]" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <ProgressBar value={0} />
              <ProgressBar value={0.5} />
              <ProgressBar value={1} />
            </div>
          </CardContent>
        </Card>

        {/* RowId */}
        <Card>
          <CardHeader>
            <CardTitle>Row Id</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;RowId&gt;</code>
            </CardAction>
            <CardDescription>Monospace ID rendered inside data-table rows (no copy affordance)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <RowId id={42} />
              <RowId id={1337} />
              <RowId id="abc-def-123" />
            </div>
          </CardContent>
        </Card>

        {/* SaveDiscardPane */}
        <Card>
          <CardHeader>
            <CardTitle>Save / Discard Pane</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;SaveDiscardPane&gt;</code>
            </CardAction>
            <CardDescription>Footer pane for dirty forms — Discard resets, Save submits</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SaveDiscardPane onDiscard={() => toast.info("Discarded")} />
            <SaveDiscardPane onDiscard={() => toast.info("Discarded")} saving />
            <SaveDiscardPane onDiscard={() => toast.info("Discarded")} saveDisabled />
          </CardContent>
        </Card>

        {/* Schedule Labels */}
        <Card>
          <CardHeader>
            <CardTitle>Schedule Labels</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;ScheduleLabelChip&gt;</code>
            </CardAction>
            <CardDescription>Capture-mode swatches + legend used by the store schedule timeline</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ScheduleLabelLegend />
            <div className="flex flex-wrap items-center gap-2">
              {(["model", "human", "qr", "off"] as const).map((label) => (
                <ScheduleLabelChip
                  key={label}
                  label={label}
                  selected={selectedScheduleLabel === label}
                  onClick={() => setSelectedScheduleLabel(label)}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Schedule Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Schedule Timeline</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;ScheduleTimeline&gt;</code>
            </CardAction>
            <CardDescription>
              24h capture timeline. Editable static windows (drag edges, click for label); open hours on the rail above
              the track (and shaded on the backdrop); strict shows capture pins.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Static — editable (open 06:00–22:00)</p>
              <ScheduleTimeline
                strategy="static"
                segments={scheduleSegments}
                editable
                openWindow={{ start: 360, end: 1320 }}
                onChange={setScheduleSegments}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Strict — read-only capture times</p>
              <ScheduleTimeline
                strategy="strict"
                segments={[360, 540, 720, 900, 1080, 1260].map((t) => ({ start: t, end: t, label: "model" }))}
                openWindow={{ start: 360, end: 1320 }}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                Static — day-specific hours via <code className="font-mono">openWindowNote</code> (hover the rail)
              </p>
              <ScheduleTimeline
                strategy="static"
                segments={[
                  { start: 600, end: 780, label: "model" },
                  { start: 780, end: 960, label: "human" },
                ]}
                openWindow={{ start: 600, end: 960 }}
                openWindowNote="Sunday trading"
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                Static — invalid windows ringed via <code className="font-mono">invalidIndices</code>; the 09:00–09:01
                sliver stays visible
              </p>
              <ScheduleTimeline
                strategy="static"
                segments={[
                  { start: 360, end: 480, label: "model" },
                  { start: 540, end: 541, label: "qr" },
                  { start: 600, end: 780, label: "model" },
                  { start: 720, end: 900, label: "human" },
                ]}
                invalidIndices={[2, 3]}
                openWindow={{ start: 360, end: 1320 }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Opening hours */}
        <Card>
          <CardHeader>
            <CardTitle>Opening Hours</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;OpeningHoursList&gt;</code>
            </CardAction>
            <CardDescription>
              The store's default hours followed by its `store_opening_hours` overrides, general to specific. A row
              another one displaces for the same days is dimmed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <OpeningHoursList
              defaultOpenTime="07:00:00"
              defaultCloseTime="22:00:00"
              entries={[
                { scope: "chain", dayOfWeek: "Sun", openTime: "11:00:00", closeTime: "15:00:00" },
                { scope: "store", dayOfWeek: "Sun", openTime: "10:00:00", closeTime: "16:00:00" },
                {
                  scope: "store",
                  dayOfWeek: "*",
                  dayOfYear: "12/25",
                  description: "Christmas Day",
                  tickets: ["CIN-1996"],
                },
                {
                  scope: "store",
                  dayOfWeek: "*",
                  specificDate: "2026-12-27",
                  openTime: "12:00:00",
                  closeTime: "14:00:00",
                },
              ]}
            />
          </CardContent>
        </Card>

        {/* Separator */}
        <Card>
          <CardHeader>
            <CardTitle>Separator</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Separator&gt;</code>
            </CardAction>
            <CardDescription>Horizontal divider between content sections</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">Content above the separator</p>
            <Separator />
            <p className="text-sm">Content below the separator</p>
          </CardContent>
        </Card>

        {/* Skeleton */}
        <Card>
          <CardHeader>
            <CardTitle>Skeleton</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Skeleton&gt;</code>
            </CardAction>
            <CardDescription>Loading placeholders in various shapes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="h-24 w-full" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-20 rounded-md" />
              <Skeleton className="h-8 w-20 rounded-md" />
              <Skeleton className="h-8 w-20 rounded-md" />
            </div>
          </CardContent>
        </Card>

        {/* Slider */}
        <Card>
          <CardHeader>
            <CardTitle>Slider</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Slider&gt;</code>
            </CardAction>
            <CardDescription>
              Single-handle slider for scalar values, or two-handle range slider for spans
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label>Volume</Label>
                <span className="font-mono text-sm tabular-nums text-muted-foreground">{singleSlider[0]}</span>
              </div>
              <Slider min={0} max={100} step={1} value={singleSlider} onValueChange={setSingleSlider} />
            </div>
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label>Store hours</Label>
                <span className="font-mono text-sm tabular-nums text-muted-foreground">
                  {`${String(Math.floor((rangeSlider[0] ?? 0) / 60)).padStart(2, "0")}:${String((rangeSlider[0] ?? 0) % 60).padStart(2, "0")}`}
                  {" – "}
                  {`${String(Math.floor((rangeSlider[1] ?? 0) / 60)).padStart(2, "0")}:${String((rangeSlider[1] ?? 0) % 60).padStart(2, "0")}`}
                </span>
              </div>
              <Slider
                min={0}
                max={23 * 60 + 59}
                step={15}
                value={rangeSlider}
                onValueChange={setRangeSlider}
                minStepsBetweenThumbs={1}
              />
            </div>
          </CardContent>
        </Card>

        {/* StatusIndicator */}
        <Card>
          <CardHeader>
            <CardTitle>Status Indicator</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;StatusIndicator&gt;</code>
            </CardAction>
            <CardDescription>Displays active, inactive, or deleted status as a colored badge</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <StatusIndicator active={true} />
              <StatusIndicator active={false} />
              <StatusIndicator active={false} deleted={true} />
            </div>
          </CardContent>
        </Card>

        {/* StoreLink */}
        <Card>
          <CardHeader>
            <CardTitle>Store Link</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;StoreLink&gt;</code>
            </CardAction>
            <CardDescription>
              Linked store reference pointing to the store detail page: leading store icon + legible name. Hidden via
              showIcon=false where the context is unambiguous (e.g. the stores list).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              <StoreLink storeName="acme-001" legibleName="Acme Downtown" />
              <StoreLink storeName="acme-002" legibleName="Acme Riverside (no icon)" showIcon={false} />
              <StoreLink storeName="acme-003" />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card>
          <CardHeader>
            <CardTitle>Tabs</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Tabs&gt;</code>
            </CardAction>
            <CardDescription>Tabbed navigation with animated indicator for switching between views</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsIndicator />
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
                <TabsTrigger value="analytics">Analytics</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-4">
                <p className="text-sm text-muted-foreground">
                  This is the overview tab content. It shows general information.
                </p>
              </TabsContent>
              <TabsContent value="settings" className="mt-4">
                <p className="text-sm text-muted-foreground">
                  This is the settings tab content. Configuration options go here.
                </p>
              </TabsContent>
              <TabsContent value="analytics" className="mt-4">
                <p className="text-sm text-muted-foreground">
                  This is the analytics tab content. Charts and metrics go here.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Toasts */}
        <Card>
          <CardHeader>
            <CardTitle>Toasts (Sonner)</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">toast()</code>
            </CardAction>
            <CardDescription>Click to trigger different toast types</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => toast.success("Operation completed successfully!")}>
                Success
              </Button>
              <Button variant="outline" onClick={() => toast.error("Something went wrong. Please try again.")}>
                Error
              </Button>
              <Button variant="outline" onClick={() => toast.warning("This action cannot be undone.")}>
                Warning
              </Button>
              <Button variant="outline" onClick={() => toast.info("New version available. Refresh to update.")}>
                Info
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tooltips */}
        <Card>
          <CardHeader>
            <CardTitle>Tooltips</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;Tooltip&gt;</code>
            </CardAction>
            <CardDescription>Contextual hints shown on hover for icons, buttons, and text</CardDescription>
          </CardHeader>
          <CardContent>
            <TooltipProvider>
              <div className="flex flex-wrap items-center gap-4">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Info className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This is an info tooltip</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline">Hover me</Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tooltip on a button</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-sm underline decoration-dotted">Underlined hint text</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tooltip on inline text</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </CardContent>
        </Card>

        {/* UserLink */}
        <Card>
          <CardHeader>
            <CardTitle>User Link</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;UserLink&gt;</code>
            </CardAction>
            <CardDescription>
              Linked user reference pointing to the user detail page: leading user icon + display name (full name or
              email). Hidden via showIcon=false where the context is unambiguous (e.g. the users list).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              <UserLink publicId="0BADF00D" legibleName="Jane Doe" />
              <UserLink publicId="0BADF00D" legibleName="jane.doe@example.com (no icon)" showIcon={false} />
            </div>
          </CardContent>
        </Card>

        {/* VendorLink */}
        <Card>
          <CardHeader>
            <CardTitle>Vendor Link</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;VendorLink&gt;</code>
            </CardAction>
            <CardDescription>
              Vendor reference with the sidebar vendor icon + name, linking to the vendor detail page. Used in the
              imports "Uploaded By" column; falls back to plain text when the vendor can't be resolved. Icon hidden via
              showIcon=false where unambiguous (e.g. the vendors list).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              <VendorLink vendorName="acme_distribution" legibleName="acme_distribution" />
              <VendorLink vendorName="globex_foods" legibleName="globex_foods (no icon)" showIcon={false} />
              <VendorLink legibleName="#0BADF00D (unresolved, no link)" />
            </div>
          </CardContent>
        </Card>

        {/* Virtual Multi Select Popover */}
        <Card>
          <CardHeader>
            <CardTitle>Virtual Multi Select Popover</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;VirtualMultiSelectPopover&gt;</code>
            </CardAction>
            <CardDescription>
              Searchable multi-select dropdown with virtualized list and selected items as removable pills
            </CardDescription>
          </CardHeader>
          <CardContent className="max-w-sm">
            <VirtualMultiSelectPopover
              values={virtualMultiSelectValues}
              onChange={setVirtualMultiSelectValues}
              options={[
                { label: "admin", value: "admin" },
                { label: "corporate_user", value: "corporate_user" },
                { label: "developer", value: "developer" },
                { label: "focal_employee", value: "focal_employee" },
                { label: "focal_ops", value: "focal_ops" },
                { label: "ops_manager", value: "ops_manager" },
                { label: "ops_technician", value: "ops_technician" },
              ]}
              placeholder="Select groups"
              searchPlaceholder="Search groups..."
              emptyText="No groups found."
            />
          </CardContent>
        </Card>

        {/* Virtual Select Popover */}
        <Card>
          <CardHeader>
            <CardTitle>Virtual Select Popover</CardTitle>
            <CardAction>
              <code className="text-xs font-mono text-muted-foreground">&lt;VirtualSelectPopover&gt;</code>
            </CardAction>
            <CardDescription>
              Searchable single-select dropdown with virtualized list — pins selected item to top
            </CardDescription>
          </CardHeader>
          <CardContent className="max-w-sm">
            <VirtualSelectPopover
              value={virtualSelectValue}
              onChange={setVirtualSelectValue}
              options={[
                { label: "Europe/Berlin", value: "Europe/Berlin" },
                { label: "America/New_York", value: "America/New_York" },
                { label: "America/Los_Angeles", value: "America/Los_Angeles" },
                { label: "Asia/Tokyo", value: "Asia/Tokyo" },
                { label: "Australia/Sydney", value: "Australia/Sydney" },
              ]}
              placeholder="Select timezone"
              searchPlaceholder="Search timezones..."
              emptyText="No timezones found."
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
